import { useEffect, useRef, useState, useCallback } from 'react';
import {
  listAdminPlans,
  createAdminPlan,
  updateAdminPlan,
  deleteAdminPlan,
  toggleAdminPlanStatus,
  formatDuration,
  durationToDays,
  type CreatePlanInput,
} from '@/services/plans';
import { listAdminProducts } from '@/services/products';
import type { ProductPlan, PlanIntervalUnit, BillingType } from '@/domain/types';
import type { Product } from '@/domain/types';

// ── Helpers ──────────────────────────────────────────────────────────────────

const CURRENCIES = ['USD', 'EUR', 'GBP', 'BDT', 'AUD', 'CAD'];

function statusBadge(active: boolean) {
  return active
    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
    : 'bg-surface-100 text-surface-500 ring-1 ring-surface-200';
}

function fmtPrice(price: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(price);
}

// ── Stepper ───────────────────────────────────────────────────────────────────

function Stepper({
  value,
  min = 1,
  max = 99,
  onChange,
  label,
}: {
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  label?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-9 h-9 rounded-lg border border-surface-300 flex items-center justify-center text-surface-600 hover:bg-surface-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-semibold"
      >
        <i className="ri-subtract-line text-sm" />
      </button>
      <div className="w-16 h-9 rounded-lg border border-surface-300 flex items-center justify-center font-semibold text-surface-900 text-sm bg-white tabular-nums">
        {value}
      </div>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-9 h-9 rounded-lg border border-surface-300 flex items-center justify-center text-surface-600 hover:bg-surface-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-semibold"
      >
        <i className="ri-add-line text-sm" />
      </button>
      {label && <span className="text-sm text-surface-500">{label}</span>}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function Empty({ onNew }: { onNew: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-14 h-14 rounded-2xl bg-brand-50 flex items-center justify-center mb-4">
        <i className="ri-price-tag-3-line text-2xl text-brand-500" />
      </div>
      <h3 className="text-base font-semibold text-surface-900 mb-1">No plans yet</h3>
      <p className="text-sm text-surface-500 mb-5 max-w-xs">
        Create subscription plans for your products. Customers will choose a plan when purchasing.
      </p>
      <button
        onClick={onNew}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors"
      >
        <i className="ri-add-line" /> New Plan
      </button>
    </div>
  );
}

// ── Plan Drawer (create / edit) ───────────────────────────────────────────────

const DEFAULT_FORM: CreatePlanInput = {
  name: '',
  description: '',
  price: 9,
  currency: 'USD',
  billingType: 'subscription',
  intervalUnit: 'month',
  intervalCount: 1,
  siteLimit: 1,
  unlimited: false,
  features: [''],
  isPopular: false,
  sortOrder: 0,
  active: true,
};

interface DrawerProps {
  plan: ProductPlan | null;
  onClose: () => void;
  onSaved: () => void;
}

function PlanDrawer({ plan, onClose, onSaved }: DrawerProps) {
  const isEdit = Boolean(plan);
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    listAdminProducts().then(setProducts).catch(() => {});
  }, []);

  const [form, setForm] = useState<CreatePlanInput>(() => ({
    ...DEFAULT_FORM,
    ...(plan
      ? {
          productId:     plan.productId,
          name:          plan.name,
          description:   plan.description,
          price:         plan.price,
          currency:      plan.currency,
          billingType:   plan.billingType,
          intervalUnit:  plan.intervalUnit,
          intervalCount: plan.intervalCount,
          siteLimit:     plan.siteLimit === 999 ? 1 : plan.siteLimit,
          unlimited:     plan.siteLimit === 999,
          features:      plan.features.length > 0 ? plan.features : [''],
          isPopular:     plan.isPopular,
          sortOrder:     plan.sortOrder,
          active:        plan.status === 'active',
        }
      : {}),
  }));

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const drawerRef = useRef<HTMLDivElement>(null);

  const set = <K extends keyof typeof form>(key: K, val: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const setFeature = (i: number, val: string) =>
    setForm((f) => {
      const arr = [...f.features];
      arr[i] = val;
      return { ...f, features: arr };
    });

  const addFeature = () => setForm((f) => ({ ...f, features: [...f.features, ''] }));
  const removeFeature = (i: number) =>
    setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  const validate = () => {
    if (!form.name.trim())   return 'Plan name is required.';
    if (form.price < 0)      return 'Price cannot be negative.';
    if (form.intervalCount < 1) return 'Duration must be at least 1.';
    if (!form.unlimited && form.siteLimit < 1) return 'Site limit must be at least 1.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');

    try {
      setSaving(true);
      const input: CreatePlanInput = { ...form };
      if (isEdit && plan) {
        await updateAdminPlan(plan.id, input);
      } else {
        await createAdminPlan(input);
      }
      onSaved();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const durationPreview = form.billingType === 'subscription'
    ? `${formatDuration(form.intervalUnit, form.intervalCount)} · ${durationToDays(form.intervalUnit, form.intervalCount)} days`
    : 'Lifetime access';

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 shrink-0">
          <div>
            <h2 className="text-base font-bold text-surface-900">
              {isEdit ? 'Edit Plan' : 'New Plan'}
            </h2>
            <p className="text-xs text-surface-500 mt-0.5">
              {isEdit ? `Editing "${plan?.name}"` : 'Create a subscription plan for a product'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Scrollable body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {error && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <i className="ri-error-warning-line text-base mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          {/* Product selector */}
          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
              Product <span className="text-red-500">*</span>
            </label>
            <select
              value={form.productId || ''}
              onChange={(e) => set('productId', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
              required
            >
              <option value="">Select a product</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Name + Description */}
          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
              Plan Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Monthly, Yearly, Starter, Professional"
              className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
              Short Description
            </label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="e.g. Best for individual developers"
              className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
            />
          </div>

          {/* Price */}
          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
              Price
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm font-medium">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => set('price', parseFloat(e.target.value) || 0)}
                  className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                />
              </div>
              <select
                value={form.currency}
                onChange={(e) => set('currency', e.target.value)}
                className="w-24 px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white"
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Billing type */}
          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-2">
              Billing Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['subscription', 'one_time'] as BillingType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => set('billingType', t)}
                  className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    form.billingType === t
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-surface-200 hover:border-surface-300 text-surface-600'
                  }`}
                >
                  <i className={`${t === 'subscription' ? 'ri-loop-right-line' : 'ri-medal-line'} text-base`} />
                  {t === 'subscription' ? 'Subscription' : 'One-time / Lifetime'}
                </button>
              ))}
            </div>
          </div>

          {/* Duration — only for subscription */}
          {form.billingType === 'subscription' && (
            <div className="bg-surface-50 rounded-xl p-4 border border-surface-200">
              <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-3">
                Billing Duration
              </label>

              {/* Unit selector */}
              <div className="flex rounded-lg border border-surface-300 overflow-hidden mb-4 bg-white">
                {(['day', 'month', 'year'] as PlanIntervalUnit[]).map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => {
                      set('intervalUnit', u);
                      if (u === 'year'  && form.intervalCount > 10) set('intervalCount', 1);
                      if (u === 'month' && form.intervalCount > 24) set('intervalCount', 1);
                      if (u === 'day'   && form.intervalCount > 365) set('intervalCount', 30);
                    }}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      form.intervalUnit === u
                        ? 'bg-brand-600 text-white'
                        : 'text-surface-600 hover:bg-surface-100'
                    }`}
                  >
                    {u.charAt(0).toUpperCase() + u.slice(1)}
                  </button>
                ))}
              </div>

              {/* Count stepper (month / year) or input (day) */}
              {form.intervalUnit === 'day' ? (
                <div className="flex items-center gap-3">
                  <div className="w-28">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={form.intervalCount}
                      onChange={(e) => set('intervalCount', Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full px-3 py-2 rounded-lg border border-surface-300 text-sm text-center font-semibold focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                    />
                  </div>
                  <span className="text-sm text-surface-500">
                    {form.intervalCount === 1 ? 'Day' : 'Days'}
                  </span>
                </div>
              ) : (
                <Stepper
                  value={form.intervalCount}
                  min={1}
                  max={form.intervalUnit === 'year' ? 10 : 24}
                  onChange={(v) => set('intervalCount', v)}
                  label={form.intervalUnit === 'year'
                    ? (form.intervalCount === 1 ? 'Year' : 'Years')
                    : (form.intervalCount === 1 ? 'Month' : 'Months')}
                />
              )}

              <p className="mt-3 text-xs text-surface-400 flex items-center gap-1.5">
                <i className="ri-information-line" />
                {durationPreview}
              </p>
            </div>
          )}

          {/* Site limit */}
          <div className="bg-surface-50 rounded-xl p-4 border border-surface-200">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-semibold text-surface-500 uppercase tracking-wide">
                Site Limit
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <div
                  onClick={() => set('unlimited', !form.unlimited)}
                  className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${
                    form.unlimited ? 'bg-brand-600' : 'bg-surface-300'
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    form.unlimited ? 'translate-x-4' : 'translate-x-0.5'
                  }`} />
                </div>
                <span className="text-xs font-medium text-surface-600">Unlimited</span>
              </label>
            </div>
            {!form.unlimited && (
              <Stepper
                value={form.siteLimit}
                min={1}
                max={100}
                onChange={(v) => set('siteLimit', v)}
                label={form.siteLimit === 1 ? 'Site' : 'Sites'}
              />
            )}
            {form.unlimited && (
              <p className="text-xs text-surface-400 flex items-center gap-1.5">
                <i className="ri-checkbox-circle-line text-brand-500" />
                Customer can activate on any number of sites
              </p>
            )}
          </div>

          {/* Features */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-surface-500 uppercase tracking-wide">
                Features
              </label>
              <button
                type="button"
                onClick={addFeature}
                className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1"
              >
                <i className="ri-add-line" /> Add feature
              </button>
            </div>
            <div className="space-y-2">
              {form.features.map((feat, i) => (
                <div key={i} className="flex items-center gap-2">
                  <i className="ri-check-line text-brand-500 shrink-0" />
                  <input
                    type="text"
                    value={feat}
                    onChange={(e) => setFeature(i, e.target.value)}
                    placeholder={`Feature ${i + 1}`}
                    className="flex-1 px-3 py-2 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeFeature(i)}
                    disabled={form.features.length === 1}
                    className="w-7 h-7 flex items-center justify-center rounded text-surface-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Options row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
                Sort Order
              </label>
              <input
                type="number"
                min="0"
                value={form.sortOrder}
                onChange={(e) => set('sortOrder', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 text-center"
              />
              <p className="text-xs text-surface-400 mt-1">Lower = shown first</p>
            </div>

            <div className="flex flex-col gap-3 pt-1">
              {/* Popular toggle */}
              <label className="flex items-center justify-between gap-3 cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-surface-700">Mark as Popular</p>
                  <p className="text-xs text-surface-400">Shows a "Popular" badge</p>
                </div>
                <div
                  onClick={() => set('isPopular', !form.isPopular)}
                  className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer shrink-0 ${
                    form.isPopular ? 'bg-amber-500' : 'bg-surface-300'
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    form.isPopular ? 'translate-x-4' : 'translate-x-0.5'
                  }`} />
                </div>
              </label>

              {/* Active toggle */}
              <label className="flex items-center justify-between gap-3 cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-surface-700">Active</p>
                  <p className="text-xs text-surface-400">Visible to customers</p>
                </div>
                <div
                  onClick={() => set('active', !form.active)}
                  className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer shrink-0 ${
                    form.active ? 'bg-emerald-500' : 'bg-surface-300'
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    form.active ? 'translate-x-4' : 'translate-x-0.5'
                  }`} />
                </div>
              </label>
            </div>
          </div>

        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-200 flex gap-3 shrink-0 bg-surface-50">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="plan-form"
            onClick={handleSubmit as unknown as React.MouseEventHandler}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
          >
            {saving && <i className="ri-loader-4-line animate-spin" />}
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Plan'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Delete confirm modal ──────────────────────────────────────────────────────

function DeleteModal({ plan, onCancel, onConfirm, deleting }: {
  plan: ProductPlan;
  onCancel: () => void;
  onConfirm: () => void;
  deleting: boolean;
}) {
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm" onClick={onCancel} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-delete-bin-line text-xl text-red-500" />
          </div>
          <h3 className="text-base font-bold text-surface-900 text-center mb-1">Delete Plan?</h3>
          <p className="text-sm text-surface-500 text-center mb-5">
            <strong className="text-surface-700">"{plan.name}"</strong> will be permanently deleted.
            Existing licenses using this plan will not be affected.
          </p>
          <div className="flex gap-3">
            <button onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-lg border border-surface-300 text-surface-700 text-sm font-medium hover:border-surface-400 transition-colors">
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={deleting}
              className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
            >
              {deleting && <i className="ri-loader-4-line animate-spin" />}
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Plan row ──────────────────────────────────────────────────────────────────

function PlanRow({
  plan,
  onEdit,
  onDelete,
  onToggle,
  toggling,
}: {
  plan: ProductPlan;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  toggling: boolean;
}) {
  const isActive = plan.status === 'active';

  return (
    <tr className="border-b border-surface-100 hover:bg-surface-50/60 transition-colors group">
      {/* Plan name */}
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-surface-900">{plan.name}</span>
          {plan.isPopular && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-600 ring-1 ring-amber-200">
              <i className="ri-star-fill text-[9px]" /> Popular
            </span>
          )}
        </div>
        {plan.description && (
          <p className="text-xs text-surface-400 mt-0.5 max-w-xs truncate">{plan.description}</p>
        )}
      </td>

      {/* Product */}
      <td className="px-5 py-3.5">
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-50 border border-surface-200 text-xs font-medium text-surface-600">
          <i className="ri-box-3-line text-surface-400" />
          {plan.productName || (plan.productId ? plan.productId.substring(0, 8) + '...' : '—')}
        </span>
      </td>

      {/* Price */}
      <td className="px-5 py-3.5">
        <span className="text-sm font-bold text-surface-900">
          {fmtPrice(plan.price, plan.currency)}
        </span>
        {plan.billingType === 'subscription' && (
          <span className="text-xs text-surface-400 ml-0.5">
            /{plan.intervalUnit === 'month' ? 'mo' : plan.intervalUnit === 'year' ? 'yr' : 'day'}
          </span>
        )}
      </td>

      {/* Duration */}
      <td className="px-5 py-3.5">
        {plan.billingType === 'subscription' ? (
          <div className="flex items-center gap-1.5 text-sm text-surface-600">
            <i className="ri-time-line text-surface-400" />
            {formatDuration(plan.intervalUnit, plan.intervalCount)}
          </div>
        ) : (
          <span className="text-sm text-surface-500 italic">Lifetime</span>
        )}
      </td>

      {/* Sites */}
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-1.5 text-sm text-surface-600">
          <i className="ri-global-line text-surface-400" />
          {plan.siteLimit === 999 ? (
            <span className="font-semibold text-brand-600">∞ Unlimited</span>
          ) : (
            <span>{plan.siteLimit} {plan.siteLimit === 1 ? 'site' : 'sites'}</span>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="px-5 py-3.5">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${statusBadge(isActive)}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-surface-400'}`} />
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </td>

      {/* Actions */}
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onToggle}
            disabled={toggling}
            title={isActive ? 'Deactivate' : 'Activate'}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-colors disabled:opacity-40"
          >
            <i className={toggling ? 'ri-loader-4-line animate-spin' : isActive ? 'ri-toggle-fill text-emerald-600' : 'ri-toggle-line'} />
          </button>
          <button
            onClick={onEdit}
            title="Edit"
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-brand-50 text-surface-400 hover:text-brand-600 transition-colors"
          >
            <i className="ri-edit-line" />
          </button>
          <button
            onClick={onDelete}
            title="Delete"
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-surface-400 hover:text-red-500 transition-colors"
          >
            <i className="ri-delete-bin-line" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminPlans() {
  const [plans, setPlans]     = useState<ProductPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [allProducts, setAllProducts] = useState<Product[]>([]);

  const [drawerOpen, setDrawerOpen]       = useState(false);
  const [editingPlan, setEditingPlan]     = useState<ProductPlan | null>(null);
  const [deletingPlan, setDeletingPlan]   = useState<ProductPlan | null>(null);
  const [confirmDeleting, setConfirmDeleting] = useState(false);
  const [togglingId, setTogglingId]       = useState<string | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [p, prods] = await Promise.all([listAdminPlans(), listAdminProducts()]);
      setPlans(p);
      setAllProducts(prods);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load plans.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenNew = () => {
    setEditingPlan(null);
    setDrawerOpen(true);
  };

  const handleEdit = (plan: ProductPlan) => {
    setEditingPlan(plan);
    setDrawerOpen(true);
  };

  const handleSaved = () => {
    setDrawerOpen(false);
    setEditingPlan(null);
    load();
  };

  const handleToggle = async (plan: ProductPlan) => {
    setTogglingId(plan.id);
    try {
      await toggleAdminPlanStatus(plan.id, plan.status !== 'active');
      setPlans((prev) =>
        prev.map((p) =>
          p.id === plan.id ? { ...p, status: p.status === 'active' ? 'inactive' : 'active' } : p
        )
      );
    } catch {
      // silently revert
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPlan) return;
    setConfirmDeleting(true);
    try {
      await deleteAdminPlan(deletingPlan.id);
      setPlans((prev) => prev.filter((p) => p.id !== deletingPlan.id));
      setDeletingPlan(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete plan.');
    } finally {
      setConfirmDeleting(false);
    }
  };

  const visible = plans.filter((p) => {
    if (productFilter && p.productId !== productFilter) return false;
    const q = search.toLowerCase();
    return p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
  });

  const activePlans = plans.filter((p) => p.status === 'active').length;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">Plans</h1>
          <p className="text-sm text-surface-500 mt-1">
            Manage subscription plans across all your products.
          </p>
        </div>
        <button
          onClick={handleOpenNew}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors shrink-0 shadow-sm"
        >
          <i className="ri-add-line" /> New Plan
        </button>
      </div>

      {/* Stats row */}
      {!loading && plans.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Total Plans', value: plans.length, icon: 'ri-price-tag-3-line', color: 'text-brand-600 bg-brand-50' },
            { label: 'Active Plans', value: activePlans, icon: 'ri-check-double-line', color: 'text-emerald-600 bg-emerald-50' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-surface-200 p-4">
              <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center mb-2`}>
                <i className={`${s.icon} text-sm`} />
              </div>
              <p className="text-xl font-bold text-surface-900">{s.value}</p>
              <p className="text-xs text-surface-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-surface-200 px-5 py-3.5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plans…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          />
        </div>
        <select
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white min-w-[180px]"
        >
          <option value="">All Products</option>
          {allProducts.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2.5 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          <i className="ri-error-warning-line text-base shrink-0" />
          {error}
          <button onClick={load} className="ml-auto text-xs underline underline-offset-2">Retry</button>
        </div>
      )}

      {/* Table card */}
      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        {loading ? (
          <div className="px-5 py-16 text-center">
            <i className="ri-loader-4-line animate-spin text-3xl text-surface-300" />
            <p className="text-sm text-surface-400 mt-3">Loading plans…</p>
          </div>
        ) : visible.length === 0 ? (
          plans.length === 0
            ? <Empty onNew={handleOpenNew} />
            : (
              <div className="px-5 py-16 text-center">
                <i className="ri-filter-3-line text-3xl text-surface-300" />
                <p className="text-sm text-surface-500 mt-3">No plans match your filters.</p>
                <button onClick={() => setSearch('')} className="mt-3 text-xs text-brand-600 underline">
                  Clear filters
                </button>
              </div>
            )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-surface-200 bg-surface-50/60">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide w-10">Plan</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Product</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Price</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Duration</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Sites</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Status</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {visible.map((plan) => (
                  <PlanRow
                    key={plan.id}
                    plan={plan}
                    onEdit={() => handleEdit(plan)}
                    onDelete={() => setDeletingPlan(plan)}
                    onToggle={() => handleToggle(plan)}
                    toggling={togglingId === plan.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <PlanDrawer
          plan={editingPlan}
          onClose={() => { setDrawerOpen(false); setEditingPlan(null); }}
          onSaved={handleSaved}
        />
      )}

      {/* Delete modal */}
      {deletingPlan && (
        <DeleteModal
          plan={deletingPlan}
          onCancel={() => setDeletingPlan(null)}
          onConfirm={handleDeleteConfirm}
          deleting={confirmDeleting}
        />
      )}
    </div>
  );
}
